import { FieldSubject, SustainabilityQuestion } from '@hb/domain';
import { PropertyStoreService } from '@hb/modules/property/property-store.service';
import { money } from '@hb/builder';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';

import { Money } from '@ap/interface-bspc-core';
import { Property, PropertyType } from '@ap/ap-interface-apix-hypotheken-berekening';

@Injectable({
    providedIn: 'root'
})
export class SustainabilityStoreService extends AbstractStoreInjectOption<SustainabilityQuestion> implements OnDestroy {
    protected readonly store = inject(PropertyStoreService);
    protected readonly lastQuestion = SustainabilityQuestion.SUMMARY;

    private readonly sustainabilityLoanSubject: FieldSubject<boolean>;
    private readonly sustainabilitySubject: FieldSubject<Money>;

    constructor() {
        super();
        this.init();
        const initialValues = this.getInitialValues();
        this.sustainabilityLoanSubject = new FieldSubject<boolean>(initialValues?.sustainability_loan);
        this.sustainabilitySubject = new FieldSubject<Money>(initialValues?.sustainability_amount);

        this.subscriptions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.activeQuestionSubject.unsubscribe();
        this.sustainabilityLoanSubject.unsubscribe();
        this.sustainabilitySubject.unsubscribe();
    }

    update(): void {
        this.store.setSustainabilityLoan(this.sustainabilityLoanSubject.value);
        this.store.setSustainability(this.sustainabilitySubject.value);
    }

    isActiveQuestion(question: SustainabilityQuestion): boolean {
        switch (question) {
            case SustainabilityQuestion.SUSTAINABILITY_LOAN_BUTTON:
                return true;
            case SustainabilityQuestion.SUSTAINABILITY_AMOUNT:
                return this.getSustainabilityLoan();
            case SustainabilityQuestion.SUMMARY:
            default:
                return false;
        }
    }

    getSustainabilityLoan(): boolean {
        return this.sustainabilityLoanSubject.value;
    }

    getSustainability(): Money {
        return this.sustainabilitySubject.value;
    }

    setSustainabilityLoan(sustainabilityLoan: boolean): void {
        this.sustainabilityLoanSubject.next(sustainabilityLoan);
    }

    setSustainability(sustainability: string): void {
        this.sustainabilitySubject.next(money(sustainability));
    }

    getInitialValues(): Property {
        return this.store.getPropertyState();
    }

    private subscriptions(): void {
        this.sustainabilityLoanSubject.asObservable().subscribe((sustainabilityLoan) => {
            if (sustainabilityLoan) {
                if (this.sustainabilitySubject.value) {
                    this.update();
                }
            } else {
                this.sustainabilitySubject.next(null);
                this.update();
            }

            this.nextQuestion();
        });

        this.sustainabilitySubject.asObservable().subscribe(() => {
            this.update();
        });

        this.store.getPropertyType().subscribe((propertyType: PropertyType) => {
            if (propertyType === PropertyType.NONE) {
                this.sustainabilityLoanSubject.next(null);
                this.sustainabilitySubject.next(null);
            }
        });
    }
}
